<template>
  <button-toggle-group
    v-model="localValue.userType"
    :items="userTypeItems"
    class="mt-4 mb-6 button-group-wrapper"
    density="comfortable"
    bg-color="indigo-faint"
  />
  <div class="bold-label" v-if="localValue.userType === AccessPermissionType.SPECIFIC_USERS">
    <template v-if="!hideGroups">
      <v-checkbox
        v-model="localValue.includeGroups"
        :label="$t('modals.allowCloudService.step4.protectedGroups')"
        class="mt-2"
        hide-details
        :ripple="false"
      />
      <v-autocomplete
        v-if="localValue.includeGroups"
        v-model="localValue.groups"
        class="mt-6"
        :loading="groupsLoading"
        :items="groupsList"
        :label="$t('modals.allowCloudService.step4.groups')"
        :placeholder="$t('modals.allowCloudService.step4.enterGroups')"
        :rules="[atLeastOneIsRequired()]"
        variant="outlined"
        multiple
        no-filter
        return-object
        :hide-no-data="!groupSearchTerm?.length"
        :no-data-text="$t('modals.addAccessPermissions.group.noData')"
        data-testid="add-access-restrictions-group-autocomplete"
        item-value="groupId"
        item-title="serviceName"
        clear-on-select
        v-model:search="groupSearchTerm"
        @update:search="onGroupsSearchUpdate($event)"
      >
        <template v-slot:chip="{ props, item }">
          <v-chip
            v-bind="props"
            :closable="true"
            variant="flat"
            size="default"
            close-icon="$closeCircle"
            :text="item.title"
            color="indigo-faint"
          >
            <div class="d-flex align-center">
              <coro-icon class="service-icon" :icon-name="service"></coro-icon>
              <span class="ml-1 mr-2">{{ item.raw.groupName }}</span>
            </div>
          </v-chip>
        </template>
        <template #item="{ props, item }">
          <v-list-item v-bind="props" title="">
            <div
              class="d-flex align-center"
              :data-testid="`add-access-restrictions-group-${item.raw.groupName}`"
            >
              <coro-icon class="service-icon" :icon-name="service"></coro-icon>
              <span class="ml-1">{{ item.raw.groupName }}</span>
            </div>
          </v-list-item>
        </template>
      </v-autocomplete>
    </template>

    <v-checkbox
      v-model="localValue.includeUsers"
      :label="$t('modals.allowCloudService.step4.specificUsers')"
      class="mt-2"
      hide-details
      :ripple="false"
    />
    <v-autocomplete
      v-if="localValue.includeUsers"
      v-model="localValue.users"
      data-testid="add-user-modal-combobox"
      class="mt-3 coro-scrollable-combobox"
      :loading="usersLoading"
      :items="usersList"
      :label="$t('modals.allowCloudService.step4.emails')"
      :placeholder="$t('modals.allowCloudService.step4.enterEmails')"
      :rules="[atLeastOneIsRequired(), usersRule]"
      variant="outlined"
      multiple
      no-filter
      return-object
      hide-no-data
      item-value="email"
      item-title="email"
      clear-on-select
      @update:model-value="handleChange()"
      @update:search="onUsersSearchUpdate($event)"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          close-icon="$closeCircle"
          :text="item.title"
          :color="getChipColor(item.raw.email, Patterns.EMAIL)"
        >
          <div class="d-flex align-center justify-space-between">
            <span class="ml-1 mr-2">{{ item.raw.email }}</span>
          </div>
        </v-chip>
      </template>
      <template #item="{ props, item }">
        <v-list-item
          v-bind="props"
          :data-testid="`item-${camelCase(item.raw.email)}`"
          :title="item.raw.email"
        >
        </v-list-item>
      </template>
    </v-autocomplete>

    <v-checkbox
      v-model="localValue.includeLabels"
      :label="$t('modals.allowCloudService.step4.userLabels')"
      class="mt-2"
      hide-details
      :ripple="false"
    />

    <v-autocomplete
      v-if="localValue.includeLabels"
      v-model="localValue.labels"
      class="mt-3"
      :items="labelsList"
      :label="$t('modals.allowCloudService.step4.userLabels')"
      :placeholder="$t('modals.allowCloudService.step4.userLabels')"
      :rules="[atLeastOneIsRequired()]"
      variant="outlined"
      item-value="id"
      :no-data-text="$t('modals.addAccessPermissions.label.noData')"
      :loading="labelsLoading"
      item-title="name"
      clear-on-select
      return-object
      multiple
      v-model:search="labelSearchTerm"
      @update:search="onLabelsSearchUpdate($event)"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          size="default"
          :color="item.raw.color"
          density="compact"
          :text="item.raw.name"
          variant="flat"
          closable
          close-icon="$close"
          label
          rounded="3"
        >
          <span class="coro-label text-uppercase text-primary" v-text="item.raw.name" />
        </v-chip>
      </template>
      <template #item="{ item, props }">
        <v-list-item v-bind="props" title="">
          <div class="d-flex align-center label-popup-block">
            <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
            <span>
              {{ item.raw.name }}
            </span>
          </div>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import CoroIcon from "@/components/CoroIcon.vue";
import {
  type MonitoredScopeUser,
  useAccessPermissionsStore,
} from "@/_store/cloud-apps/access-permissions.module.ts";
import debounce from "lodash/debounce";
import uniqBy from "lodash/uniqBy";
import { Patterns } from "@coronet/cui";
import { AccessPermissionType, Service } from "@/constants/cloud-apps.ts";
import { atLeastOneIsRequired } from "@/_helpers/validators.ts";
import camelCase from "lodash/camelCase";
import { useI18n } from "vue-i18n";
import { useSecurityAwarenessModule } from "@/_store/security-awareness/security-awareness.module.ts";
import type { UserLabel } from "@/_store/users-settings/user-labels.module.ts";
import ButtonToggleGroup, { type ButtonToggleItem } from "@/components/ButtonToggleGroup.vue";

export interface AssignUsersForm {
  userType?: AccessPermissionType;
  groups: { groupName: string; groupId: string }[];
  users: { email: string }[];
  labels: UserLabel[];
  includeUsers: boolean;
  includeGroups: boolean;
  includeLabels: boolean;
}

export default defineComponent({
  name: "AssignUsers",
  components: { CoroIcon, ButtonToggleGroup },
  props: {
    groups: {
      type: Object as PropType<{ groupName: string; groupId: string }[]>,
      required: true,
    },
    users: {
      type: Object as PropType<{ email: string }[]>,
      required: true,
    },
    labels: {
      type: Object as PropType<UserLabel[]>,
      required: true,
    },
    userType: {
      type: String,
      required: true,
    },
    hideGroups: {
      type: Boolean,
    },
    service: {
      type: String as PropType<Service | string>,
      required: true,
    },
  },
  emits: ["model-change"],
  setup(props, { emit }) {
    const accessPermissionsStore = useAccessPermissionsStore();
    const securityAwarenessStore = useSecurityAwarenessModule();

    const localValue = ref({
      userType: props.userType || AccessPermissionType.ALL_USERS,
      groups: props.groups?.length ? [...props.groups] : [],
      users: props.users?.length ? [...props.users] : [],
      labels: props.labels?.length ? [...props.labels] : [],
      includeUsers: !!props.users?.length,
      includeGroups: !!props.groups?.length,
      includeLabels: !!props.labels?.length,
    });
    const i18n = useI18n();
    const groups = ref<{ groupName: string; groupId: string }[]>([]);
    const users = ref<{ email: string }[]>([]);
    const labels = ref<UserLabel[]>([]);
    const groupSearchTerm = ref<string | undefined>("");
    const labelSearchTerm = ref<string | undefined>("");
    const usersLoading = ref(false);
    const groupsLoading = ref(false);
    const labelsLoading = ref(false);

    const onGroupsSearchUpdate = debounce(async function (value) {
      groups.value = [];

      if (!value) {
        return;
      }

      groupsLoading.value = true;
      const responseData = await securityAwarenessStore.searchGroups({
        searchTerm: value,
        service: isCustomDomain(props.service) ? Service.CORONET : (props.service as Service),
      });
      groups.value = uniqBy([...localValue.value.groups, ...responseData], "groupId");
      groupsLoading.value = false;
    }, 500);

    const onUsersSearchUpdate = debounce(async function (email) {
      if (!email) {
        return;
      }

      usersLoading.value = true;

      try {
        users.value = await accessPermissionsStore.searchProtectedUsers({
          email,
          serviceName: isCustomDomain(props.service) ? Service.CORONET : (props.service as Service),
        });
      } catch (e) {
        console.error(e);
      }
      usersLoading.value = false;
    }, 500);

    const onLabelsSearchUpdate = debounce(async function (name) {
      if (!name) {
        return;
      }

      labelsLoading.value = true;

      try {
        const responseData = await accessPermissionsStore.searchLabels(name);

        labels.value = uniqBy([...localValue.value.labels, ...responseData], "id").filter(
          (label) => label.usersCount
        );
      } catch (e) {
        console.error(e);
      }
      labelsLoading.value = false;
    }, 500);

    const usersRule = (values: MonitoredScopeUser[]) => {
      if (values) {
        if (values.length === 0) return true;

        for (const value of values) {
          if (!Patterns.EMAIL.test(value.email)) {
            return i18n.t("validations.email");
          }
        }
      }
      return true;
    };

    const userTypes: AccessPermissionType[] = [
      AccessPermissionType.ALL_USERS,
      AccessPermissionType.SPECIFIC_USERS,
    ];

    const userTypeItems = computed<ButtonToggleItem[]>(() =>
      userTypes.map((type) => ({
        value: type,
        label: i18n.t(`modals.addAccessPermissions.assignUsers.userType.${type}`),
      }))
    );

    const isCustomDomain = (name: string | Service) => {
      return !Object.values(Service).includes(name as Service);
    };

    const getChipColor = (value: string, pattern: RegExp) => {
      const isValid = pattern.test(value);
      return isValid ? "indigo-faint" : "error";
    };

    const handleChange = () => {
      localValue.value.users = localValue.value.users
        .filter((u: string | MonitoredScopeUser) => !!u)
        .map((user: string | MonitoredScopeUser): MonitoredScopeUser => {
          if ((user as MonitoredScopeUser).email) {
            return user as MonitoredScopeUser;
          }
          return {
            email: user as string,
            service: Service.CORONET,
          };
        });
    };

    watch(
      () => localValue.value,
      () => {
        emit("model-change", {
          ...localValue.value,
          groups: localValue.value.includeGroups ? localValue.value.groups : [],
          users: localValue.value.includeUsers ? localValue.value.users : [],
          labels: localValue.value.includeLabels ? localValue.value.labels : [],
        });
      },
      { deep: true }
    );

    return {
      atLeastOneIsRequired,
      onGroupsSearchUpdate,
      handleChange,
      onUsersSearchUpdate,
      getChipColor,
      groupSearchTerm,
      Patterns,
      camelCase,
      labelSearchTerm,
      onLabelsSearchUpdate,
      labelsList: labels,
      labelsLoading,
      groupsList: groups,
      usersList: users,
      userTypes,
      usersRule,
      localValue,
      usersLoading,
      groupsLoading,
      AccessPermissionType,
      userTypeItems,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(*) {
  .v-field .v-chip {
    --v-chip-height: 32px;
  }

  .coro-label {
    font-size: 14px;
  }
}

.button-group-wrapper {
  width: 320px !important;
}

.service-icon {
  width: 24px;
  height: 24px;
}

.bold-label {
  :deep(.v-checkbox) {
    .v-label {
      font-weight: bold !important;
    }
  }
}

.color-preview {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  border-radius: 2px !important;
}
</style>
